import React, {useCallback, useEffect, useImperativeHandle, useMemo, useRef, useState} from 'react';
import {Tooltip} from '@mui/material';
import classNames from 'classnames';
import TimiDataGrid from '../../../../components/shared/grid';
import {
    GridRef,
    GridRowModel,
    GridColumnProps,
    ExpandedGroupsRef
} from '../../../../components/shared/grid/types';
import {
    TimeSheetGroupType,
    TimeSheetItem,
    TimeSheetItemStatus,
    UpdateStatusAbility
} from '../../../../features/schedule/types';
import {IntlShape} from 'react-intl';
import {Lx} from '../../../../i18n/consts';
import {TiminatorAbsenceTypes, TiminatorTypes} from '../../../../features/types/types';
import {Employee} from '../../../../features/employees/types';
import {PIdNameModel} from '../../../../features/projects/types';
import { useTimeSheetGroupBy } from '../../hooks/useTimeSheetGroupBy';
import useGridColumns from './hooks/columns';
import useGridGroupBy from './hooks/group-by';
import {useTimeSheetSearch} from '../../contexts/search';
import _ from 'lodash';
import {useMediaQuery} from 'react-responsive';
import {useAppSelector} from '../../../../hooks/hooks';
import {CollapseButton} from '../../../../components/shared/collapse-button';
import styles from './grid.module.scss';
import {SVGS} from '../../../../assets/images';
import StateIcon from '../../../../components/Schedule/timesheet-state-icon';
import {ActionIconButton, formatHours} from './components/cells';
import TimesheetActionsMenu from './components/actions-menu';
import {PopupMenuAction} from '../../../../components/shared/popup-menu/types';
import TimesheetMobileView from './components/timesheet-mobile-view';
import DataRowHeader from '../../../../components/Schedule/data/row-header';
import {usePlannedHours} from './hooks/planned-hours';
import {useDesktopGroupOverlay} from './hooks/use-desktop-group-overlay';
import type {Moment} from 'moment';
import {
    getTimesheetGridTotals,
    getHoursToApprove,
    getHoursToBill,
    TimeSheetGridTotals
} from '../../../../helpers/timesheet-summary-hours';

type HeaderActionConfig = {
    disabled?: boolean;
    active?: boolean;
    onClick?: () => void | Promise<void>;
};
type HeaderActionState = Record<string, HeaderActionConfig>;

interface TimeSheetGridProps {
    data: TimeSheetItem[];
    /* Group keys to re-open after returning from a shift/absence form. Stable per mount. */
    initialExpandedGroupKeys?: Array<string>;
    intl: IntlShape;
    shiftTypes: TiminatorTypes;
    absenceTypes: TiminatorAbsenceTypes;
    projects: PIdNameModel[],
    forceSwitch: boolean;
    notifySwitch: (isAny: boolean) => void;
    permissions: { approve: UpdateStatusAbility, billable: UpdateStatusAbility };
    canManageEntry: (item: TimeSheetItem) => boolean;
    canManageInternalNote: boolean;
    canViewInternalNote: boolean;
    onTimeshiftUpdate: (timeshift: TimeSheetItem) => Promise<void>;
    onAddNewShiftClick: (targetTimeshift: TimeSheetItem) => Promise<void>;
    onCreateShiftClick: () => void;
    onEditItemClick: (timeshift: TimeSheetItem) => Promise<void>;
    onCopyItemClick: (timeshift: TimeSheetItem) => Promise<void>;
    getShiftEmployee: (timeshift: TimeSheetItem) => Promise<Employee>;
    onDeleteItemClick: (timeshift: TimeSheetItem) => void;
    onApproveAllClick: (items: TimeSheetItem[]) => Promise<void>;
    onBillAllClick: (items: TimeSheetItem[]) => Promise<void>;
    onDisapproveAllClick: (items: TimeSheetItem[]) => Promise<void>;
    onCancelBillAllClick: (items: TimeSheetItem[]) => Promise<void>;
    onInlineApproveClick: (item: TimeSheetItem, approve: boolean) => Promise<void>;
    onInlineBillClick: (item: TimeSheetItem, billable: boolean) => Promise<void>;
    minEditableDate?: Moment;
    maxEditableDate?: Moment;
    disabled?: boolean;
}

interface DateRange {
    startDate: string;
    endDate: string;
}

interface TopLevelGroup {
    key: string;
    name: string;
    field: 'employeeName' | 'targetName';
    items: TimeSheetItem[];
    totals: TimeSheetGridTotals;
    approvable: number;
    billable: number;
    hideTrackingHours: boolean;
}

interface OverlayDebugWindow extends Window {
    __TIMESHEET_OVERLAY_DEBUG__?: boolean;
    __TIMESHEET_OVERLAY_DIAG__?: Record<string, unknown>;
}

const OVERLAY_DEBUG_STORAGE_KEY = 'timesheet.overlay.debug';

const isOverlayDebugEnabled = (): boolean => {
    if (typeof window === 'undefined') {
        return false;
    }

    const debugWindow = window as OverlayDebugWindow;
    if (typeof debugWindow.__TIMESHEET_OVERLAY_DEBUG__ === 'boolean') {
        return debugWindow.__TIMESHEET_OVERLAY_DEBUG__;
    }

    try {
        const persisted = window.localStorage.getItem(OVERLAY_DEBUG_STORAGE_KEY);
        if (persisted !== null) {
            return persisted === '1';
        }
    } catch {
        // no-op: ignore storage access issues
    }

    return process.env.NODE_ENV !== 'production';
};

const overlayDebugLog = (event: string, payload?: unknown) => {
    if (!isOverlayDebugEnabled()) {
        return;
    }

    if (payload === undefined) {
        console.debug('[TimesheetOverlay]', event);
        return;
    }

    console.debug('[TimesheetOverlay]', event, payload);
};

const writeOverlayDiag = (payload: Record<string, unknown>) => {
    if (typeof window === 'undefined' || !isOverlayDebugEnabled()) {
        return;
    }

    const debugWindow = window as OverlayDebugWindow;
    debugWindow.__TIMESHEET_OVERLAY_DIAG__ = {
        ...(debugWindow.__TIMESHEET_OVERLAY_DIAG__ ?? {}),
        ...payload,
        updatedAt: new Date().toISOString(),
    };
};

const getTopLevelGroupField = (groupBy: TimeSheetGroupType): 'employeeName' | 'targetName' => (
    groupBy === TimeSheetGroupType.EMPLOYEEVIEW ? 'employeeName' : 'targetName'
);

const getRangeAndEmployee = (
    items: TimeSheetItem[],
    fallback?: DateRange | null,
    fallbackEmployeeId?: number | null
) => {
    const employeeIdFromItems =
        items.find((item) => (item?.employeeId ?? 0) > 0)?.employeeId ?? items[0]?.employeeId;
    const employeeId = employeeIdFromItems ?? fallbackEmployeeId ?? null;

    if (!employeeId || !fallback?.startDate || !fallback?.endDate) {
        return null;
    }

    return {
        employeeId,
        startDate: fallback.startDate,
        endDate: fallback.endDate
    };
};

const TimeSheetGrid = (props: TimeSheetGridProps, ref: React.Ref<ExpandedGroupsRef>) => {
    const { data, forceSwitch, permissions, disabled = false } = props;
    const isMobile = useMediaQuery({ maxWidth: 1000 });
    const isCompactDesktop = useMediaQuery({ minWidth: 1001, maxWidth: 1100 });
    const isCompactTextColumns = useMediaQuery({ minWidth: 1001, maxWidth: 1250 });
    const [isAnyRowExpanded, setIsAnyRowExpanded] = useState(false);
    const { value: search } = useTimeSheetSearch();
    const { groupBy } = useTimeSheetGroupBy();
    const employees = useAppSelector((selector) => selector.rEmployees);
    const dateRange = useAppSelector((selector) => selector.timeTrackingFilters.filter?.dateRangeFilter);
    const fallbackRange = useMemo(() => {
        if (!dateRange?.startDate || !dateRange?.endDate) {
            return null;
        }
        return { startDate: dateRange.startDate, endDate: dateRange.endDate };
    }, [dateRange?.endDate, dateRange?.startDate]);

    const ordered = useMemo(() => {

        if (groupBy === TimeSheetGroupType.PROJECTVIEW) {
            return _.chain(data)
                .orderBy((x) => x.employeeName)
                .orderBy((x) => x.date)
                .orderBy((x) => x.targetName)
                .orderBy((x) => x.timeshiftType)
                .orderBy((x) => x.hours === 0)                
                .value();
        }

        return _.chain(data)
            .orderBy((x) => x.date)
            .orderBy((x) => x.employeeName)
            .orderBy((x) => x.hours === 0)
            .value();
    }, [data, groupBy]);

    const rows = useMemo(() => {
        const trimSearch = search.trim();
        if (trimSearch === ''){
            return ordered;
        }

        const toSearch = trimSearch.toLowerCase();
        return ordered.filter((s) => {
            return s.employeeName.toLowerCase().includes(toSearch)
                || s.targetName.toLowerCase().includes(toSearch)
                || (s.note !== undefined && s.note !== null && s.note.toLowerCase().includes(toSearch))
                || (props.canViewInternalNote && s.internalNote !== undefined && s.internalNote !== null && s.internalNote.toLowerCase().includes(toSearch));
        })
    }, [ordered, props.canViewInternalNote, search])
    const rowsMap = useMemo(() => _.keyBy(rows, 'id'), [rows]);
    const gridRef = useRef<GridRef>(null);
    const prevOverlayRenderedRef = useRef(false);
    const prevOverlayPinnedRef = useRef(false);

    // Read on demand: the grid stays the single source of truth for what is open.
    // On mobile the data grid is not mounted, so this reports nothing.
    useImperativeHandle(ref, () => ({
        getExpandedGroupKeys: () => gridRef.current?.getExpandedRowKeys() ?? []
    }), []);

    const topLevelGroupField = useMemo(
        () => getTopLevelGroupField(groupBy),
        [groupBy]
    );

    const topLevelGroups = useMemo<TopLevelGroup[]>(() => {
        const groups = new Map<string, TimeSheetItem[]>();

        rows.forEach((item) => {
            const rawKey = topLevelGroupField === 'employeeName' ? item.employeeName : item.targetName;
            const key = `${rawKey ?? ''}`;
            if (key === '') {
                return;
            }

            const state = groups.get(key);
            if (state) {
                state.push(item);
                return;
            }
            groups.set(key, [item]);
        });

        return Array.from(groups.entries()).map(([key, items]) => {
            const totals = getTimesheetGridTotals(items);

            return {
                key,
                name: key,
                field: topLevelGroupField,
                items,
                totals,
                approvable: getHoursToApprove(items),
                billable: getHoursToBill(items),
                hideTrackingHours: topLevelGroupField === 'targetName'
            };
        });
    }, [rows, topLevelGroupField]);

    const topLevelGroupMap = useMemo(
        () => new Map<string, TopLevelGroup>(topLevelGroups.map((group) => [group.key, group])),
        [topLevelGroups]
    );
    const {
        gridWrapperRef,
        customHeaderRef,
        overlayRef,
        overlayState,
        isLayoutTransitioning,
        setOverlayHidden,
        recalculateOverlay,
        scheduleOverlayRecalculation,
        handleOverlayCollapseActiveGroup
    } = useDesktopGroupOverlay({
        isMobile,
        topLevelGroupMap,
        onDebugLog: overlayDebugLog,
        onWriteDiag: writeOverlayDiag
    });

    const totals = useMemo(() => getTimesheetGridTotals(rows), [rows]);

    const approvableHours = useMemo(() => getHoursToApprove(rows), [rows]);
    const billableHoursToBill = useMemo(
        () => Math.max(totals.billable - totals.billed, 0),
        [totals.billable, totals.billed]
    );
    const effectivePermissions = useMemo(
        () => disabled
            ? {
                approve: { canApply: false, canCancel: false },
                billable: { canApply: false, canCancel: false },
            }
            : permissions,
        [disabled, permissions]
    );
    const approveActive = totals.approvable > 0 && totals.approved >= totals.approvable;
    const billableActive = totals.billable > 0 && totals.billed >= totals.billable;
    const approveButtonDisabled = rows.length === 0 || !(effectivePermissions.approve?.canApply || effectivePermissions.approve?.canCancel);
    const hasBillableShift = totals.billable > 0;
    const billableButtonDisabled = rows.length === 0
        || !(effectivePermissions.billable?.canApply || effectivePermissions.billable?.canCancel)
        || !hasBillableShift;
    const actionsButtonDisabled = disabled || rows.length === 0;
    const canApproveActions = effectivePermissions.approve?.canApply || effectivePermissions.approve?.canCancel;
    const canBillActions = effectivePermissions.billable?.canApply || effectivePermissions.billable?.canCancel;

    const handleInlineApproveClick = useCallback((item: TimeSheetItem, approve: boolean) => {
        if (disabled) {
            return Promise.resolve();
        }
        return props.onInlineApproveClick(item, approve);
    }, [disabled, props.onInlineApproveClick]);

    const handleInlineBillableClick = useCallback((item: TimeSheetItem, billable: boolean) => {
        if (disabled) {
            return Promise.resolve();
        }
        return props.onInlineBillClick(item, billable);
    }, [disabled, props.onInlineBillClick]);

    const handleApproveAllClick = useCallback(async (items: Array<TimeSheetItem>) => {
        if (disabled) {
            return;
        }
        await props.onApproveAllClick(items);
    }, [disabled, props.onApproveAllClick]);

    const handleBillAllClick = useCallback(async (items: Array<TimeSheetItem>) => {
        if (disabled) {
            return;
        }
        await props.onBillAllClick(items);
    }, [disabled, props.onBillAllClick]);

    const handleDisapproveAllClick = useCallback(async (items: Array<TimeSheetItem>) => {
        if (disabled) {
            return;
        }
        await props.onDisapproveAllClick(items);
    }, [disabled, props.onDisapproveAllClick]);

    const handleCancelAllBillingClick = useCallback(async (items: Array<TimeSheetItem>) => {
        if (disabled) {
            return;
        }
        await props.onCancelBillAllClick(items);
    }, [disabled, props.onCancelBillAllClick]);

    const handleAnyRowExpand = useCallback((isAnyRowExpand: boolean) => {
        setIsAnyRowExpanded(isAnyRowExpand);
        props.notifySwitch(isAnyRowExpand);
    }, [props.notifySwitch]);

    const handleApproveHeaderClick = useCallback(() => {
        if (approveButtonDisabled) {
            return;
        }
        if (approveActive) {
            return handleDisapproveAllClick(rows);
        }
        return handleApproveAllClick(rows);
    }, [approveActive, approveButtonDisabled, handleApproveAllClick, handleDisapproveAllClick, rows]);

    const handleBillableHeaderClick = useCallback(() => {
        if (billableButtonDisabled) {
            return;
        }
        if (billableActive) {
            return handleCancelAllBillingClick(rows);
        }
        return handleBillAllClick(rows);
    }, [billableActive, billableButtonDisabled, handleBillAllClick, handleCancelAllBillingClick, rows]);

    const handleActionsHeaderClick = useCallback(() => {
        const template = rows[0];
        if (actionsButtonDisabled || !template || !props.canManageEntry(template)) {
            return;
        }
        props.onCreateShiftClick();
    }, [actionsButtonDisabled, props.canManageEntry, props.onCreateShiftClick, rows]);

    const headerColumns = useGridColumns(
        groupBy,
        props.shiftTypes,
        props.absenceTypes,
        props.projects,
        handleInlineApproveClick,
        handleInlineBillableClick,
        props.onAddNewShiftClick,
        props.onEditItemClick,
        props.onCopyItemClick,
        props.getShiftEmployee,
        props.onDeleteItemClick,
        effectivePermissions.approve,
        effectivePermissions.billable,
        props.canManageEntry,
        props.canManageInternalNote,
        props.canViewInternalNote,
        true,
        true,
        disabled,
        isCompactDesktop,
        isCompactTextColumns,
        props.minEditableDate,
        props.maxEditableDate
    );

    const columns = useGridColumns(
        groupBy,
        props.shiftTypes,
        props.absenceTypes,
        props.projects,
        handleInlineApproveClick,
        handleInlineBillableClick,
        props.onAddNewShiftClick,
        props.onEditItemClick,
        props.onCopyItemClick,
        props.getShiftEmployee,
        props.onDeleteItemClick,
        effectivePermissions.approve,
        effectivePermissions.billable,
        props.canManageEntry,
        props.canManageInternalNote,
        props.canViewInternalNote,
        false,
        false,
        disabled,
        isCompactDesktop,
        isCompactTextColumns,
        props.minEditableDate,
        props.maxEditableDate
    );

    const summaryFields = useMemo(
        () => [
            'plannedHours',
            'workHours',
            'absenceHours',
            'approvedHours',
            'billableHours',
            'totalHours',
            'approveAction',
            'billableAction',
            'actions'
        ],
        []
    );
    const visibleHeaderColumns = useMemo(
        () => headerColumns.filter((column) => column.visible === undefined || column.visible),
        [headerColumns]
    );
    const summaryColumns = useMemo(
        () => visibleHeaderColumns.filter((column) => summaryFields.includes(column.field)),
        [summaryFields, visibleHeaderColumns]
    );
    const collapseColumnTemplate = isCompactDesktop ? 'minmax(110px, 1.15fr)' : 'minmax(130px, 1.25fr)';
    const templateColumnsValue = useMemo(
        () => [
            collapseColumnTemplate,
            ...summaryColumns.map((column: GridColumnProps) => {
                const width = column.width ? `${column.width}px` : 'auto';
                const flexed = column.flex !== undefined
                    ? typeof column.flex === 'boolean'
                        ? 'minmax(0, 1fr)'
                        : `minmax(0, ${column.flex.grow}fr)`
                    : null;

                return flexed ?? width;
            })
        ].join(' '),
        [summaryColumns]
    );
    const headerTemplateColumns = useMemo(
        () => ({ gridTemplateColumns: templateColumnsValue }),
        [templateColumnsValue]
    );
    const summaryColumnIndexes = useMemo(
        () => {
            const indexes: Record<string, number> = {};
            summaryColumns.forEach((column, index) => {
                indexes[column.field] = index + 1;
            });
            return indexes;
        },
        [summaryColumns]
    );
    const actionHeaderIcons = useMemo<Record<string, React.ReactNode>>(
        () => ({
            approveAction: <StateIcon status={TimeSheetItemStatus.APPROVED} active />,
            billableAction: <StateIcon status={TimeSheetItemStatus.BILLABLE} active />,
            actions: <SVGS.ActionIcon />
        }),
        []
    );
    const actionHeaderVariants = useMemo<Record<string, 'approve' | 'billable' | 'default'>>(
        () => ({
            approveAction: 'approve',
            billableAction: 'billable',
            actions: 'default'
        }),
        []
    );
    const collapseColumnEnd = summaryColumns.length > 0 ? 2 : 1;
    const headerActionState: HeaderActionState = useMemo(
        () => ({
            approveAction: {
                disabled: approveButtonDisabled,
                onClick: handleApproveHeaderClick,
                active: approveActive
            },
            billableAction: {
                disabled: billableButtonDisabled,
                onClick: handleBillableHeaderClick,
                active: billableActive
            }
        }),
        [
            approveActive,
            approveButtonDisabled,
            billableActive,
            billableButtonDisabled,
            handleApproveHeaderClick,
            handleBillableHeaderClick
        ]
    );

    const approveAllLabel = useMemo(
        () => approveActive
            ? props.intl.formatMessage({ id: Lx.Schedule.DISAPPROVE_FORMAT_ALL }, { s: formatHours(totals.approved) })
            : props.intl.formatMessage({ id: Lx.Schedule.APPROVE_ALL_FORMAT }, { s: formatHours(approvableHours) }),
        [approveActive, approvableHours, props.intl, totals.approved]
    );

    const billAllLabel = useMemo(
        () => billableActive
            ? props.intl.formatMessage({ id: Lx.Schedule.CANCEL_BILL_FORMAT }, { s: formatHours(totals.billed) })
            : props.intl.formatMessage({ id: Lx.Schedule.BILL_ALL_FORMAT }, { s: formatHours(billableHoursToBill) }),
        [billableActive, billableHoursToBill, props.intl, totals.billed]
    );

    const addShiftLabel = useMemo(
        () => props.intl.formatMessage({ id: Lx.Schedule.CREATE_NEW_TIMESHIFT_SHIFT }),
        [props.intl]
    );

    const headerMenuActions = useMemo<PopupMenuAction[]>(() => {
        const actions: PopupMenuAction[] = [];

        if (canApproveActions) {
            actions.push({
                name: approveAllLabel,
                icon: (
                    <StateIcon
                        status={approveActive ? TimeSheetItemStatus.APPROVED : TimeSheetItemStatus.NORMAL}
                        active={approveActive}
                    />
                ),
                callback: () => handleApproveHeaderClick(),
                disabled: approveButtonDisabled
            });
        }

        if (canBillActions && hasBillableShift) {
            actions.push({
                name: billAllLabel,
                icon: <StateIcon status={TimeSheetItemStatus.BILLABLE} active={billableActive} />,
                callback: () => handleBillableHeaderClick(),
                disabled: billableButtonDisabled
            });
        }

        const canAddFromFirstRow = rows[0] ? props.canManageEntry(rows[0]) : false;
        if (canAddFromFirstRow) {
            actions.push({
                name: addShiftLabel,
                icon: <SVGS.PlusIcon />,
                callback: handleActionsHeaderClick,
                disabled: actionsButtonDisabled
            });
        }

        return actions;
    }, [
        actionsButtonDisabled,
        addShiftLabel,
        approveActive,
        approveAllLabel,
        approveButtonDisabled,
        billAllLabel,
        billableActive,
        billableButtonDisabled,
        canApproveActions,
        canBillActions,
        handleActionsHeaderClick,
        handleApproveHeaderClick,
        handleBillableHeaderClick,
        hasBillableShift,
        props.canManageEntry,
        rows
    ]);

    const gridGroupBy = useGridGroupBy(
        groupBy,
        handleApproveAllClick,
        handleBillAllClick,
        handleDisapproveAllClick,
        handleCancelAllBillingClick,
        props.onAddNewShiftClick,
        effectivePermissions.approve,
        effectivePermissions.billable,
        props.canManageEntry,
        templateColumnsValue,
        fallbackRange,
        summaryColumnIndexes,
        collapseColumnEnd,
        disabled
    )

    const collapseLabel = isAnyRowExpanded
        ? props.intl.formatMessage({ id: Lx.General.COLLAPSE_ALL })
        : props.intl.formatMessage({ id: Lx.General.EXPAND_ALL });

    const activeOverlayGroup = useMemo<TopLevelGroup | null>(() => {
        if (!overlayState.activeKey) {
            return null;
        }
        return topLevelGroupMap.get(overlayState.activeKey) ?? null;
    }, [overlayState.activeKey, topLevelGroupMap]);

    const fallbackEmployeeId = useMemo<number | null>(() => {
        if (topLevelGroupField !== 'employeeName' || !activeOverlayGroup) {
            return null;
        }
        return employees.find((employee) => employee.name === activeOverlayGroup.name)?.id ?? null;
    }, [activeOverlayGroup, employees, topLevelGroupField]);

    const activeOverlayPlannedParams = useMemo(
        () => getRangeAndEmployee(
            activeOverlayGroup?.items ?? [],
            topLevelGroupField === 'employeeName' ? fallbackRange : null,
            fallbackEmployeeId
        ),
        [
            activeOverlayGroup?.items,
            fallbackEmployeeId,
            fallbackRange?.endDate,
            fallbackRange?.startDate,
            topLevelGroupField
        ]
    );
    const { plannedHours: activeOverlayPlannedHours, isLoading: isOverlayPlannedHoursLoading } = usePlannedHours(activeOverlayPlannedParams);
    const activeOverlayPlannedValue = isOverlayPlannedHoursLoading || !activeOverlayPlannedParams
        ? 0
        : activeOverlayPlannedHours;

    const headerCells = useMemo(
        () => summaryColumns
            .map((column, index) => {
                const columnIndex = index + 1;
                const isActionField = column.field === 'approveAction'
                    || column.field === 'billableAction'
                    || column.field === 'actions';

                if (column.field === 'actions') {
                    return (
                        <div
                            key={column.field}
                            className={classNames(styles.customHeaderCell, styles.hoursHeaderCell, styles.actionsHeaderCell)}
                            style={{ gridColumn: `${columnIndex + 1} / ${columnIndex + 2}` }}
                        >
                            <TimesheetActionsMenu
                                actions={headerMenuActions}
                                disabled={actionsButtonDisabled}
                            />
                        </div>
                    );
                }
                const icon = actionHeaderIcons[column.field];
                const action = headerActionState[column.field];
                const headerLabel = column.localizationKey
                    ? props.intl.formatMessage({ id: column.localizationKey })
                    : column.field;
                const content = icon
                    ? (
                        <ActionIconButton
                            icon={icon}
                            label={headerLabel}
                            variant={actionHeaderVariants[column.field] ?? 'default'}
                            disabled={action?.disabled}
                            active={action?.active}
                            onClick={action?.onClick}
                        />
                    )
                    : headerLabel;
                const headerContent = column.headerTooltipKey
                    ? (
                        <Tooltip title={props.intl.formatMessage({ id: column.headerTooltipKey })}>
                            <span>{content}</span>
                        </Tooltip>
                    )
                    : content;

                return (
                    <div
                        key={column.field}
                        className={classNames(
                            styles.customHeaderCell,
                            styles.hoursHeaderCell,
                            { [styles.actionsHeaderCell]: isActionField }
                        )}
                        style={{ gridColumn: `${columnIndex + 1} / ${columnIndex + 2}` }}
                    >
                        {headerContent}
                    </div>
                );
            }),
        [actionHeaderIcons, actionHeaderVariants, actionsButtonDisabled, headerActionState, headerMenuActions, props.intl, summaryColumns]
    );

    useEffect(() => {
        gridRef.current?.collapseRows();
    }, [forceSwitch])

    useEffect(() => {
        recalculateOverlay();
        scheduleOverlayRecalculation();
    }, [
        groupBy,
        isAnyRowExpanded,
        recalculateOverlay,
        rows,
        scheduleOverlayRecalculation,
        summaryColumns,
        templateColumnsValue
    ]);

    useEffect(() => {
        recalculateOverlay();
        scheduleOverlayRecalculation();
    }, [
        activeOverlayGroup?.key,
        activeOverlayPlannedValue,
        isOverlayPlannedHoursLoading,
        recalculateOverlay,
        scheduleOverlayRecalculation
    ]);

    useEffect(() => {
        writeOverlayDiag({
            phase: 'heartbeat',
            isMobile,
            rows: rows.length,
            topLevelGroups: topLevelGroupMap.size,
            hasGridWrapper: !!gridWrapperRef.current,
            hasHeaderWrapper: !!customHeaderRef.current,
            activeKey: overlayState.activeKey,
        });
    }, [isMobile, overlayState.activeKey, rows.length, topLevelGroupMap.size]);

    useEffect(() => {
        if (!activeOverlayGroup) {
            return;
        }

        overlayDebugLog('overlay:group-data-ready', {
            key: activeOverlayGroup.key,
            items: activeOverlayGroup.items.length,
            total: activeOverlayGroup.totals.total,
            approved: activeOverlayGroup.totals.approved,
            billed: activeOverlayGroup.totals.billed,
            planned: activeOverlayPlannedValue,
        });
    }, [
        activeOverlayGroup,
        activeOverlayPlannedValue,
    ]);

    useEffect(() => {
        const isRendered = Boolean(activeOverlayGroup && overlayState.activeKey);
        if (prevOverlayRenderedRef.current !== isRendered) {
            overlayDebugLog(isRendered ? 'overlay:render-mounted' : 'overlay:render-unmounted', {
                activeKey: overlayState.activeKey,
            });
            prevOverlayRenderedRef.current = isRendered;
            if (!isRendered) {
                prevOverlayPinnedRef.current = false;
            }
        }
    }, [activeOverlayGroup, overlayState.activeKey]);

    useEffect(() => {
        const activeKey = overlayState.activeKey;
        if (!activeKey) {
            return;
        }

        const wrapperElement = gridWrapperRef.current;
        if (!wrapperElement) {
            return;
        }

        const activeGroupRoot = Array.from(
            wrapperElement.querySelectorAll<HTMLElement>('[data-grid-group-root="true"][data-grid-group-level="0"]')
        ).find((element) => element.dataset.gridGroupKey === activeKey);
        const isOpen = activeGroupRoot?.dataset.groupOpen === 'true';
        if (!isOpen) {
            overlayDebugLog('overlay:active-group-closed-in-dom', { activeKey });
            setOverlayHidden('active-group-closed-in-dom');
        }
    }, [overlayState.activeKey, rows.length, setOverlayHidden]);

    useEffect(() => {
        if (!overlayState.activeKey || !overlayRef.current || !customHeaderRef.current) {
            return;
        }

        const headerBottom = customHeaderRef.current.getBoundingClientRect().bottom;
        const overlayTop = overlayRef.current.getBoundingClientRect().top;
        const pinDelta = Number((overlayTop - headerBottom).toFixed(2));
        const isPinned = Math.abs(pinDelta) <= 1.5;

        if (prevOverlayPinnedRef.current !== isPinned) {
            overlayDebugLog(isPinned ? 'overlay:pinned-under-header' : 'overlay:unpinned-from-header', {
                activeKey: overlayState.activeKey,
                pinDelta,
                headerBottom: Number(headerBottom.toFixed(2)),
                overlayTop: Number(overlayTop.toFixed(2)),
            });
            prevOverlayPinnedRef.current = isPinned;
        }

        overlayDebugLog('overlay:pin-check', {
            activeKey: overlayState.activeKey,
            pinDelta,
            offsetY: Number(overlayState.offsetY.toFixed(2)),
            top: Number(overlayState.top.toFixed(2)),
        });
    }, [overlayState]);

    const handleRowEditEnd = (row: GridRowModel, state: GridRowModel) => {
        if (disabled) {
            return Promise.resolve();
        }
        const item: TimeSheetItem = {...rowsMap[row.id]}
        Object.keys(state).forEach((s) => {
            switch (s) {
                case 'date':
                    item.date = state[s] as string;
                    break;
                case 'type':
                    item.timeshiftTypeId = state[s] as number;
                    break;
                case 'note':
                    item.note = state[s] as string;
                    break;
                case 'internalNote':
                    if (!props.canManageInternalNote) {
                        break;
                    }
                    item.internalNote = state[s] as string;
                    break;
                case 'target':
                    item.targetId = state[s] as number;
                    break;
                case 'totalHours': {
                    const hours = Number(state[s]);
                    if (!Number.isNaN(hours)) {
                        item.hours = hours;
                    }
                    break;
                }
            }
        });
        const updateItem = { ...rowsMap[row.id], ...item };
        return props.onTimeshiftUpdate(updateItem);
    };

    if (isMobile) {
        return (
            <div className={classNames(styles.gridWrapper, styles.grid)}>
                <TimesheetMobileView
                    data={rows}
                    groupBy={groupBy}
                    shiftTypes={props.shiftTypes}
                    absenceTypes={props.absenceTypes}
                    intl={props.intl}
                    permissions={effectivePermissions}
                    canManageEntry={props.canManageEntry}
                    onApproveAllClick={handleApproveAllClick}
                    onBillAllClick={handleBillAllClick}
                    onDisapproveAllClick={handleDisapproveAllClick}
                    onCancelBillAllClick={handleCancelAllBillingClick}
                    onInlineApproveClick={handleInlineApproveClick}
                    onInlineBillClick={handleInlineBillableClick}
                    onEditItemClick={props.onEditItemClick}
                    onCopyItemClick={props.onCopyItemClick}
                    onDeleteItemClick={props.onDeleteItemClick}
                    onAddNewShiftClick={props.onAddNewShiftClick}
                    dateRange={fallbackRange}
                    disabled={disabled}
                />
            </div>
        );
    }

    return (
        <div
            ref={gridWrapperRef}
            className={classNames(styles.gridWrapper, styles.grid, {
                [styles.compactDesktop]: isCompactDesktop
            })}
        >
            <div ref={customHeaderRef} className={styles.customHeaderWrapper}>
                <div className={styles.customHeader} style={headerTemplateColumns}>
                    <div
                        className={classNames(styles.customHeaderCell, styles.collapseHeaderCell)}
                        style={{ gridColumn: `1 / ${collapseColumnEnd}` }}
                    >
                        <CollapseButton
                            displayText={collapseLabel}
                            isExpanded={isAnyRowExpanded}
                            onExpandClick={() => gridRef.current?.collapseRows()}
                        />
                    </div>
                    {headerCells}
                </div>
                {activeOverlayGroup && overlayState.activeKey && (
                    <div
                        ref={overlayRef}
                        className={classNames(styles.groupOverlay, {
                            [styles.groupOverlayLayoutTransitioning]: isLayoutTransitioning
                        })}
                        style={{
                            transform: `translateY(${overlayState.offsetY}px)`,
                        }}
                    >
                        <div className={styles.groupOverlayToggle}>
                            <CollapseButton
                                isExpanded={true}
                                onExpandClick={handleOverlayCollapseActiveGroup}
                            />
                        </div>
                        <DataRowHeader
                            name={activeOverlayGroup.name}
                            total={activeOverlayGroup.totals.total}
                            approved={activeOverlayGroup.totals.approved}
                            approvableHours={activeOverlayGroup.totals.approvable}
                            billed={activeOverlayGroup.totals.billed}
                            approvable={activeOverlayGroup.approvable}
                            billable={activeOverlayGroup.billable}
                            billableHours={activeOverlayGroup.totals.billable}
                            workHours={activeOverlayGroup.totals.workHours}
                            absenceHours={activeOverlayGroup.totals.absenceHours}
                            plannedHours={activeOverlayPlannedValue}
                            hideTrackingHours={activeOverlayGroup.hideTrackingHours}
                            approveAbility={effectivePermissions.approve}
                            billalbeAbility={effectivePermissions.billable}
                            onApproveAllClick={async () => await handleApproveAllClick(activeOverlayGroup.items)}
                            onBillAllClick={async () => await handleBillAllClick(activeOverlayGroup.items)}
                            onDisapproveAllClick={async () => await handleDisapproveAllClick(activeOverlayGroup.items)}
                            onCancelAllBillingClick={async () => await handleCancelAllBillingClick(activeOverlayGroup.items)}
                            onAddNewShiftClick={async () => {
                                const template = activeOverlayGroup.items[0];
                                if (!template || disabled) {
                                    return;
                                }
                                await props.onAddNewShiftClick({ ...template, date: '' });
                            }}
                            hideButtons={disabled}
                            templateColumns={templateColumnsValue}
                            summaryColumnIndexes={summaryColumnIndexes}
                            collapseColumnEnd={collapseColumnEnd}
                            intl={props.intl}
                        />
                    </div>
                )}
            </div>
            <TimiDataGrid
                hideHeader
                columns={columns}
                rows={rows}
                groupBy={gridGroupBy}
                intl={props.intl}
                onRowEditEnd={handleRowEditEnd}
                editDataLoadingMode={'server'}
                ref={gridRef}
                onAnyRowExpand={handleAnyRowExpand}
                initialExpandedRowKeys={props.initialExpandedGroupKeys}
                autoExpandSingleGroup
                className={styles.grid}
                disableBodyScroll
            />
        </div>
    );
}

export default React.forwardRef(TimeSheetGrid) as (
    props: TimeSheetGridProps & React.RefAttributes<ExpandedGroupsRef>
) => React.JSX.Element;
